'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface AnswerMarkProps {
  className?: string
  children: React.ReactNode
}

/**
 * The signature flourish: a keyword span gets a primary-light band plus a
 * 2px underline that draws left-to-right on first view — marking the correct
 * answer. Used exactly twice per page: hero + final CTA.
 */
export function AnswerMark({ className, children }: AnswerMarkProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const [drawn, setDrawn] = useState<boolean>(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      const frame = requestAnimationFrame(() => setDrawn(true))
      return () => cancelAnimationFrame(frame)
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setDrawn(true)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <span
      ref={ref}
      className={cn(
        'bg-primary-light relative inline-block rounded-[6px] px-[0.12em] py-[0.02em]',
        className
      )}
    >
      {children}
      <span
        aria-hidden="true"
        className={cn(
          'bg-primary absolute inset-x-[0.14em] -bottom-[0.06em] h-[2px] origin-left rounded-full transition-transform duration-[600ms] ease-out motion-reduce:transition-none',
          drawn ? 'scale-x-100' : 'scale-x-0'
        )}
      />
    </span>
  )
}
